import { http } from 'Utils/Http/Http';

/**
 * Every /api/v1/admin certificate endpoint in one place, so the URLs, the
 * query-string names and the shape of Laravel's pagination envelope are
 * written once instead of once per page.
 *
 * Uses the app's single axios instance (Utils/Http/Http) like every other
 * page — its request interceptor attaches the admin token, so nothing here
 * deals with auth.
 */

const NAMES_URL = '/api/v1/admin/certificate-names';
const VERIFICATIONS_URL = '/api/v1/admin/certificate-verifications';
const PRINTS_URL = '/api/v1/admin/certificate-prints';
// Not a certificate endpoint: the site-wide settings the Season 4 page edits too.
// The verification threshold is one of those settings.
const SETTINGS_URL = '/api/v1/admin/settings';

/**
 * Drops empty filters instead of sending `?search=&category=` on every
 * request. axios already omits undefined, but an untouched text field holds an
 * empty string.
 */
const compactParams = (params = {}) => {
    const compacted = {};

    Object.keys(params).forEach((key) => {
        const value = params[key];

        if (value !== undefined && value !== null && value !== '') {
            compacted[key] = value;
        }
    });

    return compacted;
};

/**
 * Normalises the pagination envelope once for all three tables.
 *
 * `serial` is the row's position in the WHOLE result set: meta.from is the
 * 1-based index of the first row on this page, so meta.from + offset numbers
 * page 2 as 26, 27, 28... The array index alone would restart at 1 on every
 * page, and the row id is a primary key, not a position.
 */
const toPaginatedList = ({ data }) => {
    const from = data?.meta?.from ?? 1;

    return {
        rows: (data?.data ?? []).map((row, index) => ({ ...row, serial: from + index })),
        total: data?.meta?.total ?? 0,
        perPage: data?.meta?.per_page,
        currentPage: data?.meta?.current_page ?? 1,
        lastPage: data?.meta?.last_page ?? 1,
    };
};

/**
 * `category: null` is how the API clears a category, so write bodies are built
 * explicitly rather than run through compactParams — that would drop the null
 * and silently leave the old category in place on an edit.
 */
const toWriteBody = (values) => ({
    name: values?.name?.trim() ?? '',
    category: values?.category ? values.category : null,
});

export const fetchCertificateNames = (params) =>
    http.get(NAMES_URL, { params: compactParams(params) }).then(toPaginatedList);

/**
 * Adds a name to one list. Without a list id the backend files it under the
 * default list, which is what this did before lists existed.
 */
export const createCertificateName = (values, listId) =>
    http
        .post(NAMES_URL, { ...toWriteBody(values), ...(listId ? { certificate_name_list_id: listId } : {}) })
        .then(({ data }) => data?.data);

export const updateCertificateName = (id, values) =>
    http.put(`${NAMES_URL}/${id}`, toWriteBody(values)).then(({ data }) => data?.data);

export const deleteCertificateName = (id) => http.delete(`${NAMES_URL}/${id}`);

/**
 * The admin twin of the public check: same response shape, minus the download
 * token. This tool reports on a name, it never issues a certificate.
 *
 * The similarity, the threshold and the pass/fail decision all come back from
 * the backend — nothing on the client recomputes them.
 */
export const verifyCertificateName = (values) =>
    http.post(`${NAMES_URL}/verify`, compactParams(values)).then(({ data }) => data?.data);

/**
 * The settings envelope carries every setting; the certificate pages only ever
 * want the threshold out of it.
 *
 * parseFloat, so "85" reads the same as 85 while a missing or null value stays
 * NaN instead of becoming Number(null)'s 0. No number means an error, not a
 * resolved undefined: a backend that predates the setting sends no threshold at
 * all, and the card unlocks Save as soon as it has a value — it must not do
 * that over one it never got.
 */
const toThreshold = ({ data }) => {
    const threshold = parseFloat(data?.data?.certificate_similarity_threshold);

    if (!Number.isFinite(threshold)) {
        throw new Error('The settings response has no certificate_similarity_threshold.');
    }

    return threshold;
};

/**
 * The similarity a name must reach to count as verified. The backend applies it
 * to the public check and to the Verify Name tool alike, so this one number
 * decides both.
 *
 * @returns {Promise<number>}
 */
export const fetchCertificateThreshold = () => http.get(SETTINGS_URL).then(toThreshold);

/**
 * Sends the threshold and nothing else. The endpoint updates only the settings
 * present in the body, so leaving the Season 4 ones out is what keeps this save
 * from writing over them.
 *
 * Resolves to the value the server echoes back, not the one sent: that is what
 * is now in force.
 *
 * @returns {Promise<number>}
 */
export const updateCertificateThreshold = (threshold) =>
    http.post(SETTINGS_URL, { certificate_similarity_threshold: threshold }).then(toThreshold);

/**
 * One attempt at the render endpoint.
 *
 * Comes back as a Blob, so a failure arrives as a Blob too rather than as the
 * JSON every other call here returns. Reading it back into `error.response.data`
 * is what lets the page handle a 422 the same way it would on any other form.
 */
const requestCertificatePdf = (values) =>
    http
        .post(`${NAMES_URL}/print`, toWriteBody(values), { responseType: 'blob' })
        .then(({ data }) => data)
        .catch(async (error) => {
            const body = error?.response?.data;

            if (body instanceof Blob) {
                try {
                    error.response.data = JSON.parse(await body.text());
                } catch {
                    // Not JSON — a gateway error page, say. Leave the Blob in
                    // place; the page falls back to a generic message.
                }
            }

            throw error;
        });

/**
 * Render a certificate for any name, approved or not.
 *
 * The deliberate hole in the certificate feature: no verification, no token, no
 * approved-list lookup — the operator types a name and gets that name's PDF. It
 * is how somebody missed off the list still gets their certificate.
 *
 * Retried when the response never arrives. The certificate is half a megabyte
 * of artwork, and `php artisan serve` — PHP's single-threaded built-in server,
 * which the dev environment points at — drops perhaps one such response in six
 * part-way through. The request reaches the server and renders fine; only the
 * transfer dies, which is why the operator was seeing an error for work that had
 * plainly succeeded.
 *
 * Three attempts, because the page no longer says anything when they are all
 * used up: at one loss in six a single attempt fails 17% of the time and three
 * fail together well under 1%.
 *
 * Safe to repeat because this endpoint writes nothing: it renders and returns.
 * No retry when the server answered, since a 422 says the same thing however
 * many times it is asked.
 *
 * @returns {Promise<Blob>} the PDF
 */
const TRANSFER_ATTEMPTS = 3;

export const printCertificateForName = async (values) => {
    let lastError;

    for (let attempt = 0; attempt < TRANSFER_ATTEMPTS; attempt++) {
        try {
            // eslint-disable-next-line no-await-in-loop
            return await requestCertificatePdf(values);
        } catch (error) {
            // The server answered — it just said no. Asking again would only
            // get the same answer.
            if (error?.response) {
                throw error;
            }

            lastError = error;
        }
    }

    throw lastError;
};

export const fetchCertificateVerifications = (params) =>
    http.get(VERIFICATIONS_URL, { params: compactParams(params) }).then(toPaginatedList);

/** The log of who actually opened a certificate for printing. */
export const fetchCertificatePrints = (params) =>
    http.get(PRINTS_URL, { params: compactParams(params) }).then(toPaginatedList);

/**
 * Empty the verification log.
 *
 * This clears the print log too: a print row is a child of the attempt that
 * produced it and the database cascades. The response says how many of each
 * went, so the confirmation the operator sees can be specific rather than
 * hopeful.
 *
 * @returns {Promise<{verifications: number, prints: number}>}
 */
export const clearCertificateVerifications = () =>
    http.delete(VERIFICATIONS_URL).then(({ data }) => data?.data ?? {});

/**
 * Empty the print log on its own, leaving every verification in place.
 *
 * @returns {Promise<{prints: number}>}
 */
export const clearCertificatePrints = () =>
    http.delete(PRINTS_URL).then(({ data }) => data?.data ?? {});

/**
 * Remove one verification attempt.
 *
 * Its prints go with it — same cascade as the bulk clear, one row wide. The
 * response says how many prints that was, so a row vanishing from the other tab
 * can be accounted for instead of looking like a glitch.
 *
 * @returns {Promise<{prints: number}>}
 */
export const deleteCertificateVerification = (id) =>
    http.delete(`${VERIFICATIONS_URL}/${id}`).then(({ data }) => data?.data ?? {});

/** Remove one print record. The verification behind it stays. */
export const deleteCertificatePrint = (id) => http.delete(`${PRINTS_URL}/${id}`);

// ------------------------------------------------------------------ lists
//
// /api/v1/admin/certificate-name-lists. Every approved name belongs to one
// list; each list is switched on and off on its own, and only the names in
// active lists verify or print.

const LISTS_URL = '/api/v1/admin/certificate-name-lists';

/**
 * Uploads go as multipart. The app's axios instance defaults to JSON, so the
 * header is set here; booleans travel as 1/0, which Laravel's boolean rule
 * accepts and the string "false" would not be.
 */
const toFormData = (fields) => {
    const form = new FormData();

    Object.keys(fields).forEach((key) => {
        const value = fields[key];

        if (value === undefined || value === null || value === '') {
            return;
        }

        form.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : value);
    });

    return form;
};

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

/** @returns {Promise<{lists: Array, activeCount: number, defaultId: number}>} */
export const fetchCertificateNameLists = () =>
    http.get(LISTS_URL).then(({ data }) => ({
        lists: data?.data ?? [],
        activeCount: data?.meta?.active ?? 0,
        defaultId: data?.meta?.default_id ?? null,
    }));

/**
 * Create a list. `names` (typed in, one per line) or `file` (a spreadsheet)
 * fill it; with neither it is created empty.
 *
 * @returns {Promise<{list: object, import: object|null}>}
 */
export const createCertificateNameList = ({ name, description, isActive, names, category, file }) => {
    const request = file
        ? http.post(
              LISTS_URL,
              toFormData({ name, description, is_active: isActive, file }),
              MULTIPART,
          )
        : http.post(LISTS_URL, {
              name,
              description: description || null,
              is_active: isActive,
              ...(names ? { names, category: category || null } : {}),
          });

    return request.then(({ data }) => ({ list: data?.data, import: data?.import ?? null }));
};

export const updateCertificateNameList = (id, { name, description }) =>
    http.put(`${LISTS_URL}/${id}`, { name, description: description || null }).then(({ data }) => data?.data);

/** @returns {Promise<{names_deleted: number}>} */
export const deleteCertificateNameList = (id) =>
    http.delete(`${LISTS_URL}/${id}`).then(({ data }) => data?.data ?? {});

export const setCertificateNameListActive = (id, active) =>
    http.post(`${LISTS_URL}/${id}/${active ? 'activate' : 'deactivate'}`).then(({ data }) => data?.data);

/** Make exactly these lists active; every other list is deactivated. */
export const setActiveCertificateNameLists = (ids) =>
    http.put(`${LISTS_URL}/active`, { list_ids: ids }).then(({ data }) => data?.data ?? []);

/** What a spreadsheet would put into a brand-new list. Writes nothing. */
export const previewCertificateNameFile = (file) =>
    http.post(`${LISTS_URL}/preview`, toFormData({ file }), MULTIPART).then(({ data }) => data?.data?.import);

/**
 * Import a spreadsheet into an existing list. With dryRun the backend works the
 * import out — including, for a replace, what would be removed — and writes
 * nothing.
 *
 * @returns {Promise<{import: object, list: object}>}
 */
export const importIntoCertificateNameList = (id, { file, mode, dryRun }) =>
    http
        .post(`${LISTS_URL}/${id}/import`, toFormData({ file, mode, dry_run: Boolean(dryRun) }), MULTIPART)
        .then(({ data }) => data?.data ?? {});

/**
 * The names in use right now: every active list's names, each name once.
 * A plain Laravel paginator, not a resource collection, so the envelope has
 * total at the top level.
 */
export const fetchActiveCertificateNames = (params) =>
    http.get(`${LISTS_URL}/active-names`, { params: compactParams(params) }).then(({ data }) => ({
        rows: data?.data ?? [],
        total: data?.total ?? 0,
    }));
